import { ScanBarcode, Search, X } from 'lucide-react'

import { forwardRef, KeyboardEvent } from 'react'

import { Kbd } from '@/components/ui'
import { CameraScanButton } from '@/components/pos/scan/CameraScanButton'

interface ProductSearchProps {
  value: string
  onChange: (value: string) => void
  /** Enter pressed: the page decides between a barcode lookup and adding the single match. */
  onSubmit: (value: string) => void
  /** A camera read, handled like the hardware scanner's. */
  onScan: (code: string) => void
}

/**
 * The cashier's home position. Holds focus between sales, accepts typed names, SKUs and scanner input alike, and
 * understands `3*` before a code to ring up three at once.
 */
export const ProductSearch = forwardRef<HTMLInputElement, ProductSearchProps>(({ value, onChange, onSubmit, onScan }, ref) => {
  const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && value.trim()) {
      e.preventDefault()
      onSubmit(value.trim())
    } else if (e.key === 'Escape') {
      onChange('')
    }
  }

  return (
    <div className="relative">
      <Search className="pointer-events-none absolute left-4 top-1/2 size-5 -translate-y-1/2 text-muted-foreground" />
      <input
        ref={ref}
        autoFocus
        value={value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={onKeyDown}
        placeholder="Search or scan a product…"
        aria-label="Search products or scan a barcode"
        autoComplete="off"
        spellCheck={false}
        className="h-12 w-full rounded-sm border border-input bg-card pl-12 pr-36 text-base text-ink shadow-z1 outline-none transition-[border-color,box-shadow] placeholder:text-placeholder-foreground hover:border-grey-400 focus-visible:border-ring focus-visible:ring-1 focus-visible:ring-ring"
      />
      <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-2">
        {value ? (
          <button
            type="button"
            onClick={() => onChange('')}
            aria-label="Clear search"
            className="grid size-7 place-items-center rounded-full text-muted-foreground hover:bg-fill"
          >
            <X className="size-4" />
          </button>
        ) : (
          <ScanBarcode className="size-5 text-muted-foreground" aria-hidden />
        )}
        <CameraScanButton continuous onScan={onScan} />
        <Kbd>F2</Kbd>
      </div>
    </div>
  )
})
ProductSearch.displayName = 'ProductSearch'
